import { useEffect, useState, useRef } from 'react';
import { api, send } from './api.js';
import { t } from './i18n.js';
import { Notice } from './components.js';
import { exactLocalToIso, browserZone } from './time.js';
export function ResourcesSync({
  role,
  calendars,
  selected,
  revision,
}: {
  role: string;
  calendars: any[];
  selected: string;
  revision?: unknown;
}) {
  const [resources, setResources] = useState<any[]>([]),
    [resource, setResource] = useState(''),
    [self, setSelf] = useState(''),
    [bookings, setBookings] = useState<any[]>([]),
    [subscriptions, setSubscriptions] = useState<any[]>([]),
    [url, setUrl] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [name, setName] = useState(''),
    [kind, setKind] = useState('room'),
    [title, setTitle] = useState(''),
    [start, setStart] = useState(''),
    [end, setEnd] = useState('');
  const key = useRef(crypto.randomUUID());
  const resourceRef = useRef(resource),
    selectedRef = useRef(selected);
  resourceRef.current = resource;
  selectedRef.current = selected;
  const load = async () => {
    setResources(await api('/resources'));
    setSelf((await api('/collaboration/capabilities')).userId);
  };
  const loadBookings = async (id: string) => {
    if (!id) return;
    const now = Date.now();
    const rows = await api(
      `/resources/${id}/bookings?from=${encodeURIComponent(new Date(now - 86400000).toISOString())}&to=${encodeURIComponent(new Date(now + 90 * 86400000).toISOString())}`,
    );
    if (resourceRef.current === id) setBookings(rows);
  };
  const loadSubscriptions = async (id: string) => {
    const rows = id ? await api(`/team-calendars/${id}/subscriptions`) : [];
    if (selectedRef.current === id) setSubscriptions(rows);
  };
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    setBookings([]);
    void loadBookings(resource).catch((e) => setError(e.message));
  }, [resource, revision]);
  useEffect(() => {
    setUrl('');
    setSubscriptions([]);
    void loadSubscriptions(selected).catch((e) => setError(e.message));
  }, [selected]);
  async function act(fn: () => Promise<void>) {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <section className="pro-card">
        <h2>{t('Salles et équipements')}</h2>
        <p>
          {t(
            'Réservez une ressource pour votre équipe. Deux réservations ne peuvent pas se chevaucher.',
          )}
        </p>
        {error && <Notice type="error">{error}</Notice>}
        {['owner', 'admin'].includes(role) && (
          <form
            className="pro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void act(async () => {
                await send('/resources', { name, kind, capacity: 1 });
                setName('');
                await load();
              });
            }}
          >
            <label>
              {t('Nom de la ressource')}
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={150}
              />
            </label>
            <label>
              {t('Type')}
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="room">{t('Salle')}</option>
                <option value="equipment">{t('Équipement')}</option>
              </select>
            </label>
            <button disabled={busy}>{t('Ajouter la ressource')}</button>
          </form>
        )}
        <label>
          {t('Ressource')}
          <select disabled={busy} value={resource} onChange={(e) => setResource(e.target.value)}>
            <option value="">{t('Choisir une ressource')}</option>
            {resources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        {resource && (
          <>
            <form
              className="pro-form"
              onSubmit={(e) => {
                e.preventDefault();
                void act(async () => {
                  await send(`/resources/${resource}/bookings`, {
                    title,
                    start: exactLocalToIso(start, browserZone()),
                    end: exactLocalToIso(end, browserZone()),
                    requestKey: key.current,
                  });
                  key.current = crypto.randomUUID();
                  setTitle('');
                  await loadBookings(resource);
                });
              }}
            >
              <label>
                {t('Objet de la réservation')}
                <input
                  required
                  maxLength={200}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
              <label>
                {t('Début de la réservation')}
                <input
                  type="datetime-local"
                  required
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
              </label>
              <label>
                {t('Fin de la réservation')}
                <input
                  type="datetime-local"
                  required
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </label>
              <button disabled={busy}>{t('Réserver ce créneau')}</button>
            </form>
            <h3>{t('Réservations des 90 prochains jours')}</h3>
            {bookings.length === 0 && <p>{t('Aucune réservation sur cette période.')}</p>}
            {bookings.map((b) => (
              <div key={b.id}>
                <strong>{b.title}</strong>
                <p>
                  {new Date(b.starts_at).toLocaleString()} – {new Date(b.ends_at).toLocaleString()}
                </p>
                {b.calendar_event_id && (
                  <p>
                    {t(
                      'Réservation liée à une réunion : modifiez ou annulez la réunion pour changer ce créneau.',
                    )}
                  </p>
                )}
                {!b.calendar_event_id &&
                  (b.owner_id === self || ['owner', 'admin'].includes(role)) && (
                    <button
                      disabled={busy}
                      onClick={() =>
                        void act(async () => {
                          await send(
                            `/resource-bookings/${b.id}`,
                            { revision: b.revision },
                            'DELETE',
                          );
                          await loadBookings(resource);
                        })
                      }
                    >
                      {t('Annuler la réservation')}
                    </button>
                  )}
              </div>
            ))}
          </>
        )}
      </section>
      <section className="pro-card">
        <h2>{t('Afficher l’agenda dans Outlook ou Thunderbird')}</h2>
        <p>
          {t(
            'Abonnement en lecture seule au calendrier sélectionné. Modifiez vos événements dans Vename Mail. La fréquence d’actualisation dépend du client.',
          )}
        </p>
        {!selected ? (
          <p>{t('Sélectionnez un calendrier d’équipe.')}</p>
        ) : (
          <>
            <p>{calendars.find((c) => c.id === selected)?.name}</p>
            <button
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  const result = await send(`/team-calendars/${selected}/subscriptions`, {});
                  if (selectedRef.current === selected) setUrl(result.url);
                  await loadSubscriptions(selected);
                })
              }
            >
              {t('Créer un lien d’abonnement privé')}
            </button>
            {url && (
              <>
                <Notice>
                  {t(
                    'Ce lien donne accès aux événements. Conservez-le comme un mot de passe. Il expire après un an et peut être révoqué.',
                  )}
                </Notice>
                <label>
                  {t('Lien iCalendar')}
                  <input readOnly value={url} onFocus={(e) => e.target.select()} />
                </label>
              </>
            )}
            <p>
              {t(
                'Outlook : Ajouter un calendrier → S’abonner à partir du web. Thunderbird : Nouveau calendrier → Sur le réseau → iCalendar (ICS).',
              )}
            </p>
            <p>{t('Le flux inclut les 90 derniers jours et les 365 prochains jours.')}</p>
            {subscriptions.map((s) => (
              <div key={s.id}>
                <p>
                  Créé le {new Date(s.created_at).toLocaleDateString()} · Expire le{' '}
                  {new Date(s.expires_at).toLocaleDateString()}
                </p>
                <button
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      await api(`/calendar-subscriptions/${s.id}`, { method: 'DELETE' });
                      setUrl('');
                      await loadSubscriptions(selected);
                    })
                  }
                >
                  {t('Révoquer cet abonnement')}
                </button>
              </div>
            ))}
          </>
        )}
      </section>
    </>
  );
}
